<template>
  <div class="closure-signature">
    <div class="signature-seal">
      <!-- Header compacto -->
      <div class="seal-header">
        <div class="d-flex align-center justify-space-between">
          <div>
            <p class="seal-title mb-0">{{ itemTypeText }}{{ itemLabel ? ' ' + itemLabel : ' Nº' + item.number }}</p>
            <p class="seal-subtitle mb-0">{{ projectName }}</p>
          </div>
					<div class="text-right">
						<v-icon color="white" size="28">mdi-shield-check-outline</v-icon>
            <p class="seal-date mb-0" v-if="item.approved_status_change">Aprobado: {{ formatDate(item.approved_status_change.created_at) }}</p>
					</div>
        </div>
      </div>

      <!-- Aprobadores en grid compacto -->
      <div class="approvers-section">
        <p class="approvers-title">{{ approversTitle }}</p>
        <div class="approvers-grid">
          <div 
            v-for="(approval, index) in signatureEntries" 
            :key="index"
            class="approver-item"
          >
            <v-icon color="#2D4B95" size="16" class="mr-1">mdi-check-circle</v-icon>
            <div class="approver-details">
              <p class="approver-name">{{ signatureUserName(approval) }}</p>
              <p class="approver-meta">{{ signatureRole(approval) }} • {{ formatDate(approval.created_at) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- QR y código en línea -->
      <div class="qr-section" v-if="item.verification_code?.qr_code_url">
        <img 
          :src="item.verification_code?.qr_code_url"
          alt="QR"
          class="qr-image"
        />
        <div class="qr-info">
          <p class="qr-label">Código:</p>
          <p class="qr-code">{{ item.verification_code?.code }}</p>
        </div>
        <div class="footer-logo">
          <img src="@/assets/Svg/logoH.svg" alt="LaOT" class="laot-logo">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'ClosureSignature',
  props: {
    item: {
      type: Object,
      required: true
    },
    itemType: {
      type: String,
      default: 'additional'
    },
    itemLabel: {
      type: String,
      default: null
    }
  },
  computed: {
    itemTypeText() {
      const types = {
        'rdi': 'RDI',
        'task': 'Tarea',
        'additional': 'Adicional',
        'entrega': 'Entrega',
        'communication_folio': 'Folio'
      }
      return types[this.itemType] || 'Documento'
    },
    signatureEntries() {
      const approvalChecks = this.item.approval_checks || []
      if (this.itemType === 'communication_folio' && this.item.current_signatures) {
        return this.item.current_signatures
      }
      return approvalChecks.length ? approvalChecks : (this.item.user_signatures || [])
    },
    approversTitle() {
      return this.itemType === 'communication_folio' ? 'Firmado por:' : 'Aprobado por:'
    },
    projectName() {
      return this.item.project?.name || this.item.project_name || '-'
    }
  },
  methods: {
    formatDate(dateString) {
      return moment(dateString).format('DD/MM/YYYY HH:mm')
    },
    signatureUserName(entry) {
      return entry?.user_name || entry?.user?.info?.name || entry?.user?.name || 'Usuario'
    },
    signatureRole(entry) {
      return entry?.job_type || entry?.jobtype?.name || (this.itemType === 'communication_folio' ? 'Firma digital' : 'Aprobación')
    },
    getUserEmail(userId) {
      if (this.item.created_by_user && this.item.created_by_user.id === userId) {
        return this.item.created_by_user.email
      }
      
      if (this.item.revisions && this.item.revisions.length > 0) {
        for (const revision of this.item.revisions) {
          if (revision.observations && revision.observations.length > 0) {
            for (const obs of revision.observations) {
              if (obs.user && obs.user.id === userId && obs.user.email) {
                return obs.user.email
              }
            }
          }
        }
      }
      
      return 'No disponible'
    }
  }
}
</script>

<style scoped>
.closure-signature {
  display: flex;
  justify-content: center;
  padding: 12px;
}

.signature-seal {
  max-width: 300px;
  width: 100%;
  background: white;
  border: 2px solid #2D4B95;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(45, 75, 149, 0.12);
  overflow: hidden;
}

.seal-header {
  background: linear-gradient(135deg, #2D4B95 0%, #3d5fc0 100%);
  padding: 10px 14px;
  color: white;
}

.seal-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.seal-subtitle {
  font-size: 10px;
  opacity: 0.85;
  margin-top: 2px;
}

.seal-date {
  font-size: 9px;
  opacity: 0.8;
  margin-top: 3px;
  font-style: italic;
}

.approvers-section {
  padding: 10px 14px;
  background: #fafbff;
}

.approvers-title {
  margin: 0 0 8px 0;
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.approvers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
}

.approver-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px;
  background: white;
  border: 1px solid #e0e7ff;
  border-radius: 8px;
}

.approver-details {
  flex: 1;
  min-width: 0;
}

.approver-name {
  margin: 0 0 2px 0;
  font-size: 11px;
  font-weight: 700;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.approver-meta {
  margin: 0;
  font-size: 9px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qr-section {
  padding: 10px 14px;
  background: white;
  border-top: 1px solid #e0e7ff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.qr-image {
  width: 55px;
  height: 55px;
  object-fit: contain;
  border: 1px solid #e0e7ff;
  border-radius: 6px;
  padding: 3px;
}

.qr-info {
  flex: 1;
}

.qr-label {
  margin: 0 0 1px 0;
  font-size: 8px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 600;
}

.qr-code {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  color: #2D4B95;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.3px;
}

.footer-logo .laot-logo {
  height: 18px;
  width: auto;
}

@media print {
  .closure-signature {
    background: white;
    padding: 0;
  }
  
  .signature-seal {
    box-shadow: none;
    page-break-inside: avoid;
  }
}
</style>
